import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import { useFlashMessage } from '../components/FlashMessage';
import ConfirmDialog from '../components/ConfirmDialog';

const APITokens = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    permissions: []
  });
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    tokenId: null,
    tokenName: ''
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();

  // Available permissions
  const availablePermissions = [
    { value: 'sms:read', label: 'SMS Okuma', description: 'Bekleyen SMS\'leri görüntüleme' },
    { value: 'sms:update', label: 'SMS Güncelleme', description: 'SMS durumunu güncelleme' },
    { value: 'vehicles:read', label: 'Araç Okuma', description: 'Araç bilgilerini görüntüleme' },
    { value: 'vehicles:write', label: 'Araç Yazma', description: 'Araç oluşturma ve güncelleme' }
  ];

  // Fetch tokens
  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAPITokens();
      setTokens(response.data.data);
    } catch (error) {
      showDanger('API token\'lar yüklenirken hata oluştu');
      console.error('Fetch tokens error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle create token
  const handleCreateToken = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showDanger('Token adı gereklidir');
      return;
    }

    if (formData.permissions.length === 0) {
      showDanger('En az bir izin seçmelisiniz');
      return;
    }

    try {
      const response = await adminAPI.createAPIToken(formData);
      setNewToken(response.data.data);
      setShowCreateModal(false);
      setShowTokenModal(true);
      setFormData({ name: '', permissions: [] });
      fetchTokens();
      showSuccess('API token başarıyla oluşturuldu');
    } catch (error) {
      showDanger(error.response?.data?.message || 'Token oluşturulurken hata oluştu');
      console.error('Create token error:', error);
    }
  };

  // Handle permission toggle
  const togglePermission = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  // Handle delete click
  const handleDeleteClick = (token) => {
    setDeleteDialog({
      isOpen: true,
      tokenId: token.id,
      tokenName: token.name
    });
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await adminAPI.deleteAPIToken(deleteDialog.tokenId);
      showSuccess(`"${deleteDialog.tokenName}" başarıyla silindi`);
      setDeleteDialog({ isOpen: false, tokenId: null, tokenName: '' });
      fetchTokens();
    } catch (error) {
      showDanger(error.response?.data?.message || 'Token silinirken hata oluştu');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (token) => {
    try {
      await adminAPI.updateAPIToken(token.id, { is_active: !token.is_active });
      showSuccess(`Token ${token.is_active ? 'devre dışı bırakıldı' : 'aktif edildi'}`);
      fetchTokens();
    } catch (error) {
      showDanger('Token güncellenirken hata oluştu');
      console.error('Toggle active error:', error);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Token panoya kopyalandı');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" style={{ borderColor: '#7a2c2c' }}></div>
      </div>
    );
  }

  return (
    <div>
      <FlashContainer />

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#333333' }}>API Anahtarları</h1>
          <p className="mt-2 text-sm" style={{ color: '#666666' }}>
            N8N ve webhook entegrasyonları için API token'larını yönetin
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          style={{ backgroundColor: '#7a2c2c' }}
        >
          + Yeni API Token
        </button>
      </div>

      {/* Tokens Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">API Anahtarları</h3>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Token Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İzinler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Son Kullanım
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Oluşturulma
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tokens.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center" style={{ color: '#666666' }}>
                  Henüz hiç API token oluşturulmamış
                </td>
              </tr>
            ) : (
              tokens.map((token) => (
                <tr key={token.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium" style={{ color: '#333333' }}>{token.name}</div>
                    <div className="text-xs" style={{ color: '#666666' }}>
                      {token.created_by_name} tarafından
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(token.permissions) ? token.permissions : []).map((perm, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: '#f3e8e8', color: '#7a2c2c' }}
                        >
                          {perm}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#666666' }}>
                    {formatDate(token.last_used_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: '#666666' }}>
                    {formatDate(token.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(token)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        token.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {token.is_active ? 'Aktif' : 'Pasif'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleDeleteClick(token)}
                      className="text-red-600 hover:text-red-900 transition-colors"
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))
            )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create Token Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h2 className="text-2xl font-bold mb-4" style={{ color: '#333333' }}>
              Yeni API Token Oluştur
            </h2>
            <form onSubmit={handleCreateToken}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: '#333333' }}>
                  Token Adı *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50"
                  style={{ focusRingColor: '#7a2c2c' }}
                  placeholder="Örn: N8N SMS Entegrasyonu"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-3" style={{ color: '#333333' }}>
                  İzinler * (En az bir tane seçmelisiniz)
                </label>
                <div className="space-y-3">
                  {availablePermissions.map((perm) => (
                    <label
                      key={perm.value}
                      className="flex items-start p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(perm.value)}
                        onChange={() => togglePermission(perm.value)}
                        className="mt-1 h-4 w-4 rounded border-gray-300"
                        style={{ accentColor: '#7a2c2c' }}
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium" style={{ color: '#333333' }}>
                          {perm.label}
                        </div>
                        <div className="text-xs" style={{ color: '#666666' }}>
                          {perm.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', permissions: [] });
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  style={{ color: '#333333' }}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#7a2c2c' }}
                >
                  Token Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Show New Token Modal */}
      {showTokenModal && newToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#333333' }}>
                Token Başarıyla Oluşturuldu!
              </h2>
              <p className="text-sm" style={{ color: '#666666' }}>
                Bu token'ı <strong>sadece bir kez</strong> göreceksiniz. Lütfen güvenli bir yere kaydedin!
              </p>
            </div>

            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium mb-2" style={{ color: '#333333' }}>
                Token Adı: {newToken.name}
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newToken.token}
                  readOnly
                  className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg font-mono text-sm"
                  style={{ color: '#333333' }}
                />
                <button
                  onClick={() => copyToClipboard(newToken.token)}
                  className="px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#7a2c2c' }}
                >
                  Kopyala
                </button>
              </div>
            </div>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium mb-2" style={{ color: '#333333' }}>
                N8N'de Kullanım:
              </p>
              <pre className="text-xs font-mono p-2 bg-white rounded border" style={{ color: '#333333' }}>
{`Headers:
  X-API-Token: ${newToken.token}`}
              </pre>
            </div>

            <button
              onClick={() => {
                setShowTokenModal(false);
                setNewToken(null);
              }}
              className="w-full px-6 py-3 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#7a2c2c' }}
            >
              Anladım, Kapat
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, tokenId: null, tokenName: '' })}
        onConfirm={handleDeleteConfirm}
        title="API Token Sil"
        message={`"${deleteDialog.tokenName}" token'ını kalıcı olarak silmek istediğinizden emin misiniz? Bu token'ı kullanan tüm entegrasyonlar çalışmayı durduracak.`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default APITokens;
